import {
  type BulkResult,
  type CountableApiResponse,
  type License,
  MeterPopulate,
  type SerializedCustomer,
  type SerializedEstablishment,
  type SerializedMeter,
  type UpdateMeterDto,
  type CreateMeterDto,
  type ViewMeters,
} from '@enmon/commons/lib/enmon';
import type { CreateRegisterDto, SerializedMeterRegister, UpdateRegisterDto } from '@enmon/commons';
import type { DeleteReadingsFilter, UpdateReadingsDto } from '@enmon/commons/lib/tools';
import type { Merge } from 'type-fest';
import { BaseApiClient } from '../baseApiClient';
import type { MeterFilter, MongoDBResponse } from '../../../types/backendGateway/metersApi';
import { joinIfArray } from 'utils/array';

export type GetMetersArgs = {
  filter?: MeterFilter & { page?: number; pageSize?: number };
  populate?: MeterPopulate[];
  view?: ViewMeters;
};
export type GetMeterResponse = PopulatedOneMeter;

export type GetMeterArgs = {
  meterId?: SerializedMeter['_id'];
  populate?: MeterPopulate[];
};
export type GetMetersResponse = CountableApiResponse<PopulatedOneMeter[]>;

export type GetMeterChildrenResponse = SerializedMeter['_id'][];

export interface UpdateMeterArg {
  meterId: SerializedMeter['_id'];
  values: UpdateMeterDto;
}
export type UpdateMeterResponse = SerializedMeter;

export type ActivateMeterArg = {
  meterId: SerializedMeter['_id'];
  update?: UpdateMeterDto;
};

export type ActivateMeterResponse = SerializedMeter;

export type MoveMeterArg = {
  meterId: SerializedMeter['_id'];
  establishmentId: string;
};

export type MoveMeterResponse = void;

export type MeterActivationArg = { meterIds: SerializedMeter['_id'][] };

export type MeterActivationResponse = void;

export type DeactivateMeterArg = { meterId: SerializedMeter['_id'] };

export type DeactivateMeterResponse = SerializedMeter;

export type MeterDeactivationArg = { meterIds: SerializedMeter['_id'][] };

export type MeterDeactivationResponse = void;

export interface DeleteMeterArg {
  meterId: SerializedMeter['_id'];
}
export type DeleteMeterResponse = void;

export type BulkDeleteMetersArg = { meterIds: SerializedMeter['_id'][] };

export type BulkDeleteMetersResponse = BulkResult;

export type BulkActivateMetersArg = { meterIds: SerializedMeter['_id'][]; license?: License };

export type BulkActivateMetersResponse = BulkResult;

export type BulkDeactivateMetersArg = { meterIds: SerializedMeter['_id'][] };

export type BulkDeactivateMetersResponse = BulkResult;

export interface CreateMeterArg {
  values: CreateMeterDto;
}
export type CreateMeterResponse = SerializedMeter;

export interface AdjustMeterCounterArg {
  meterId: SerializedMeter['_id'];
  adjustments: UpdateReadingsDto['adjustments'];
}
export type AdjustMeterCounterResponse = CountableApiResponse<MongoDBResponse>;

export interface DeleteMeterReadingsArg {
  meterId: SerializedMeter['_id'];
  filter: DeleteReadingsFilter;
}
export type DeleteMeterReadingsResponse = CountableApiResponse<MongoDBResponse>;

export interface CreateMeterRegisterArg {
  meterId: SerializedMeter['_id'];
  payload: CreateRegisterDto;
}
export type CreateMeterRegisterResponse = SerializedMeterRegister;

export interface UpdateMeterRegisterArg {
  meterId: SerializedMeter['_id'];
  registerId: SerializedMeterRegister['_id'];
  payload: UpdateRegisterDto;
}
export type UpdateMeterRegisterResponse = SerializedMeterRegister;

export interface DeleteMeterRegisterArg {
  meterId: SerializedMeter['_id'];
  registerId: SerializedMeterRegister['_id'];
}
export type DeleteMeterRegisterResponse = void;

export type PopulatedOneMeter = Merge<
  SerializedMeter,
  {
    parent: Pick<SerializedMeter, '_id' | 'name'> | null;
    establishment: SerializedEstablishment;
    customer: Pick<SerializedCustomer, '_id' | 'name'>;
  }
>;

export class BackendGatewayMetersApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('enmon-tools/meters', undefined, jwt);
  }

  public async find<T = GetMetersResponse>(
    filter: Merge<GetMetersArgs['filter'], { sorts?: string[] }> = {},
    populate: GetMetersArgs['populate'] = [MeterPopulate.Customer, MeterPopulate.Establishment, MeterPopulate.Parent],
    view?: GetMetersArgs['view'],
  ): Promise<T> {
    const { commodities, establishments, remoteReadingOrigins, customers, statuses, meterKinds, meters, toolsIds, sorts, manufacturers, meterModels } =
      filter;

    const params = {
      ...filter,
      ...(filter?.commodities ? { commodities: joinIfArray(commodities) } : {}),
      ...(filter?.remoteReadingOrigins ? { remoteReadingOrigins: joinIfArray(remoteReadingOrigins) } : {}),
      ...(filter?.customers ? { customers: joinIfArray(customers) } : {}),
      ...(filter?.establishments ? { establishments: joinIfArray(establishments) } : {}),
      ...(filter?.statuses ? { statuses: joinIfArray(statuses) } : {}),
      ...(filter?.meterKinds ? { meterKinds: joinIfArray(meterKinds) } : {}),
      ...(filter?.meters ? { meters: joinIfArray(meters) } : {}),
      ...(filter?.toolsIds ? { toolsIds: joinIfArray(toolsIds) } : {}),
      ...(filter?.sorts ? { sorts: joinIfArray(sorts) } : {}),
      ...(filter?.manufacturers ? { manufacturers: joinIfArray(manufacturers) } : {}),
      ...(filter?.meterModels ? { meterModels: joinIfArray(meterModels) } : {}),
      populate: joinIfArray(populate),
      view,
    };

    const response = await this.axios.get<T>('', { params });

    return response.data;
  }

  public async findById(
    meterId: SerializedMeter['_id'],
    populate = [MeterPopulate.Customer, MeterPopulate.Establishment, MeterPopulate.Parent],
  ): Promise<GetMeterResponse> {
    // GUARD CLAUSE: Throw early if id is missing or empty
    if (!meterId) {
      throw new Error('findById requires a valid meterId');
    }

    const response = await this.axios.get<PopulatedOneMeter>(meterId, { params: { populate: joinIfArray(populate) } });

    return response.data;
  }

  public async update({ meterId, values }: UpdateMeterArg): Promise<UpdateMeterResponse> {
    const response = await this.axios.patch<UpdateMeterResponse>(meterId, values);

    return response.data;
  }

  public async delete({ meterId }: DeleteMeterArg): Promise<DeleteMeterResponse> {
    await this.axios.delete<DeleteMeterResponse>(meterId);
  }

  public async bulkDelete({ meterIds }: BulkDeleteMetersArg): Promise<BulkDeleteMetersResponse> {
    const response = await this.axios.post<BulkDeleteMetersResponse>(`bulk-delete`, { meterIds });

    return response.data;
  }

  public async bulkActivate({ meterIds, license }: BulkActivateMetersArg): Promise<BulkActivateMetersResponse> {
    const response = await this.axios.post<BulkActivateMetersResponse>(`bulk-activate`, {
      meterIds,
      ...(license ? { license } : {}),
    });

    return response.data;
  }

  public async bulkDeactivate({ meterIds }: BulkDeactivateMetersArg): Promise<BulkDeactivateMetersResponse> {
    const response = await this.axios.post<BulkDeactivateMetersResponse>(`bulk-deactivate`, { meterIds });

    return response.data;
  }

  public async create({ values }: CreateMeterArg): Promise<CreateMeterResponse> {
    const response = await this.axios.post<CreateMeterResponse>('', values);
    return response.data;
  }

  public async activate({ meterId, update }: ActivateMeterArg): Promise<ActivateMeterResponse> {
    const response = await this.axios.patch<ActivateMeterResponse>(`${meterId}/activate`, update);

    return response.data;
  }

  public async move({ meterId, establishmentId }: MoveMeterArg): Promise<MoveMeterResponse> {
    await this.axios.post<void>(`${meterId}/move`, { establishmentId });
  }

  public async activationJob({ meterIds }: MeterActivationArg): Promise<MeterActivationResponse> {
    await this.axios.post<MeterActivationResponse>(`activate`, { meterIds });
  }

  public async deactivationJob({ meterIds }: MeterDeactivationArg): Promise<MeterDeactivationResponse> {
    await this.axios.post<MeterDeactivationResponse>(`deactivate`, {
      meterIds,
    });
  }

  public async deactivate({ meterId }: DeactivateMeterArg): Promise<DeactivateMeterResponse> {
    const response = await this.axios.patch<ActivateMeterResponse>(`${meterId}/deactivate`);

    return response.data;
  }

  public async adjustReadings({ meterId, adjustments }: AdjustMeterCounterArg): Promise<AdjustMeterCounterResponse> {
    const params = {
      adjustments,
    };

    const response = await this.axios.put<AdjustMeterCounterResponse>(`${meterId}/adjust-counter`, {
      params,
    });

    return response.data;
  }

  public async deleteReadings({ meterId, filter }: DeleteMeterReadingsArg): Promise<DeleteMeterReadingsResponse> {
    const params = { filter };

    const response = await this.axios.put<DeleteMeterReadingsResponse>(`${meterId}/delete-counter`, {
      params,
    });

    return response.data;
  }

  public async getRegisters(
    meterId: SerializedMeter['_id'],
    signal: AbortSignal | undefined,
  ): Promise<SerializedMeter['registers']> {
    const response = await this.axios.get<SerializedMeter['registers']>(`${meterId}/registers`, { signal });

    return response.data;
  }

  public async createRegister({ meterId, payload }: CreateMeterRegisterArg): Promise<CreateMeterRegisterResponse> {
    const response = await this.axios.post<CreateMeterRegisterResponse>(`${meterId}/registers`, payload);

    return response.data;
  }

  public async updateRegister({
    meterId,
    registerId,
    payload,
  }: UpdateMeterRegisterArg): Promise<UpdateMeterRegisterResponse> {
    const response = await this.axios.patch<UpdateMeterRegisterResponse>(`${meterId}/registers/${registerId}`, payload);

    return response.data;
  }

  public async deleteRegister({ meterId, registerId }: DeleteMeterRegisterArg): Promise<DeleteMeterRegisterResponse> {
    await this.axios.delete(`${meterId}/registers/${registerId}`);
  }

  public async getChildren(id: SerializedMeter['_id']): Promise<GetMeterChildrenResponse> {
    const response = await this.axios.get<GetMeterChildrenResponse>(`${id}/children`);
    return response.data;
  }
}
